/**
 * Subscription Service (Frontend)
 *
 * Handles all subscription-related API calls for the Blog-Abo-System:
 * - FCMS-Abo: All blog content except Bitcoin
 * - Bitcoin-Abo: Bitcoin content only
 * - FCMS + Bitcoin Bundle: Complete access
 */

import apiClient from '../api/apiClient';

// ============================================
// TYPES
// ============================================

export type BillingInterval = 'monthly' | 'yearly';

export interface SubscriptionPlan {
  id: string;
  slug: string;
  name: string;
  description: string;
  planType: string;
  priceMonthly: number;
  priceYearly: number | null;
  /** Backend liefert das Feld noch (Spalte bleibt in der DB), Wert ist seit Migration 160 immer 0. */
  trialDays: number | null;
  trialType: 'days' | 'limited' | null;
  maxAssets: number | null;
  maxYoutubeChannels: number | null;
  /** Max. Bilder pro Journal-Notiz. 0 = Upload deaktiviert. null = Default 3. */
  maxJournalImagesPerEntry: number | null;
  baseAsset: string | null;
  features: PlanFeature[];
  stripePriceId: string | null;
  stripePriceIdYearly: string | null;
  badgeEnabled: boolean;
  badgeText: string | null;
  isActive: boolean;
}

export interface PlanFeature {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isEnabled: boolean;
  valueLimit: number | null;
  sortOrder: number;
}

export interface UserSubscription {
  id: string;
  planId: string;
  planSlug: string;
  planName: string;
  /** Plan-Eigenschaft: enthält dieser Plan Premium-Support-Chat-Zugang? Aus billing.plans.includes_support_chat */
  includesSupportChat: boolean;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'expired';
  stripeSubscriptionId: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  createdAt: string;
  paymentProvider: 'stripe' | 'paypal' | null;
  // Plan-Wechsel Felder
  pendingPlanId?: string | null;
  pendingPlanChangeAt?: string | null;
  pendingPlanSlug?: string | null;
  pendingPlanName?: string | null;
}

export interface ContentAccess {
  hasAccess: boolean;
  canAccessAnalysis: boolean;
  canAccessStrategy: boolean;
  isTrial: boolean;
  trialAssetsUsed: number;
  trialAssetsLimit: number | null;
  trialDaysRemaining: number | null;
  requiredPlan: string | null;
  activeSubscriptions: string[];
}

export interface CheckoutSessionResponse {
  clientSecret: string;
  sessionId: string;
}

export interface ReactivationCheck {
  canReactivate: boolean;
  hasActivePeriod: boolean;
  subscriptionId: string | null;
  currentPeriodEnd: string | null;
  planName: string | null;
  paymentProvider: 'stripe' | 'paypal' | null;
  message: string | null;
}

export interface SubscriptionStatusResponse {
  subscriptions: UserSubscription[];
  hasActiveSubscription: boolean;
  /** True, wenn der Nutzer mind. ein zahlpflichtiges Pflicht-Abo (FCMS oder Bitcoin) aktiv hat. Admins immer true. */
  hasRequiredSubscription: boolean;
  hasFcmsAccess: boolean;
  hasBitcoinAccess: boolean;
}

/**
 * Pflicht-Abo-Status: Welche Module hat der User, welche könnte er noch wählen,
 * und welche Plans stehen zur Auswahl?
 */
export type PlanFamily = 'fcms' | 'bitcoin';

export interface RequirementStatus {
  hasRequired: boolean;
  activeFamilies: PlanFamily[];
  missingFamilies: PlanFamily[];
  plansByFamily: Record<PlanFamily, SubscriptionPlan[]>;
}

// Plan-Wechsel Types
export type PlanChangeType = 'upgrade' | 'downgrade' | 'downgrade_free';

export interface PlanChangePreview {
  currentPlan: { slug: string; name: string; price: number };
  newPlan: { slug: string; name: string; price: number };
  changeType: PlanChangeType;
  /** Abrechnungsintervall der aktuellen Sub. Entscheidet über "/Monat" vs "/Jahr"-Anzeige im Dialog. */
  interval?: 'monthly' | 'yearly';
  proratedAmount?: number;
  creditBalance?: number;
  creditDays?: number;
  effectiveDate: string;
  paymentProvider: 'stripe' | 'paypal';
  limitWarnings?: Array<{
    resource: string;
    current: number;
    newLimit: number | null;
    overLimit: boolean;
  }>;
}

export interface PlanChangeResult {
  success: boolean;
  changeType: PlanChangeType;
  effectiveDate: string;
  message: string;
  approvalUrl?: string;
}

// ============================================
// SERVICE FUNCTIONS
// ============================================

/**
 * Get all available subscription plans
 * Public endpoint - no auth required
 */
export const getAvailablePlans = async (): Promise<SubscriptionPlan[]> => {
  try {
    const response = await apiClient.get('/subscriptions/plans');
    return response.data.data?.plans || [];
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching plans:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Laden der Abonnement-Pläne');
  }
};

/**
 * Get user's subscription status
 * Auth required
 */
export const getSubscriptionStatus = async (): Promise<SubscriptionStatusResponse> => {
  try {
    const response = await apiClient.get('/subscriptions/status');
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching status:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Laden des Abonnement-Status');
  }
};

/**
 * Check content access for a specific asset
 * Optional auth - returns limited access for non-authenticated users
 */
export const checkContentAccess = async (assetSymbol?: string): Promise<ContentAccess> => {
  try {
    const params = assetSymbol ? `?assetSymbol=${encodeURIComponent(assetSymbol)}` : '';
    const response = await apiClient.get(`/subscriptions/access${params}`);
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error checking access:', error);
    // Return restrictive access on error
    return {
      hasAccess: false,
      canAccessAnalysis: false,
      canAccessStrategy: false,
      isTrial: false,
      trialAssetsUsed: 0,
      trialAssetsLimit: null,
      trialDaysRemaining: null,
      requiredPlan: null,
      activeSubscriptions: []
    };
  }
};

/**
 * Get active auto-apply discount (public, no auth)
 */
export interface ActiveDiscount {
  active: boolean;
  discountType?: 'percentage' | 'fixed_amount';
  discountValue?: number;
  label?: string;
  validUntil?: string | null;
}

export const getActiveDiscount = async (): Promise<ActiveDiscount> => {
  try {
    const response = await apiClient.get('/pricing/active-discount');
    return response.data.data;
  } catch {
    return { active: false };
  }
};

/**
 * Create Stripe Embedded Checkout Session for subscription purchase
 * Returns clientSecret for embedding checkout in our page
 * Auth required
 */
export const createCheckoutSession = async (
  planSlug: string,
  returnUrl?: string,
  interval: BillingInterval = 'monthly'
): Promise<CheckoutSessionResponse> => {
  try {
    const response = await apiClient.post('/subscriptions/checkout', {
      planSlug,
      returnUrl,
      interval
    });
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error creating checkout:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Erstellen der Checkout-Session');
  }
};

/**
 * Create Payment Checkout with PaymentElement (custom UI)
 * Returns client_secret for PaymentElement + subscription details
 */
export interface PaymentCheckoutResponse {
  clientSecret: string;
  subscriptionId: string;
  customerId: string;
  planName: string;
  amount: number;
  discountAmount?: number;
  discountLabel?: string;
}

export const createPaymentCheckout = async (
  planSlug: string,
  couponCode?: string,
  interval: BillingInterval = 'monthly'
): Promise<PaymentCheckoutResponse> => {
  try {
    const response = await apiClient.post('/subscriptions/payment-checkout', {
      planSlug,
      couponCode,
      interval
    });
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error creating payment checkout:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Erstellen des Checkouts');
  }
};

// =====================================================
// GUEST CHECKOUT (Schritt A des Kombi-Checkout-Wizards)
// Public-Endpoint, kein Auth nötig. Legt vorläufigen Account an und löst die
// Verifizierungs-Mail aus. Die Zahlung läuft erst NACH Klick auf den Verify-
// Link über createPaymentCheckout (Schritt B).
// =====================================================

export interface GuestCheckoutAccountData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  username?: string;
  accountType: 'private' | 'company';
  agreeToTerms: boolean;
  companyName?: string;
  address?: {
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    country: string;
  };
  betaCode?: string;
}

export interface GuestCheckoutStartResponse {
  success: true;
  /** Maskierte E-Mail zur Anzeige im Frontend, z.B. "ma***@example.com" */
  emailMasked: string;
}

export const startGuestCheckout = async (
  planSlug: string,
  interval: BillingInterval,
  accountData: GuestCheckoutAccountData,
): Promise<GuestCheckoutStartResponse> => {
  try {
    const response = await apiClient.post('/checkout/guest-start', {
      planSlug,
      interval,
      accountData,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error starting guest checkout:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Starten des Checkouts');
  }
};

/**
 * Polling-Endpoint für Cross-Device-Tab-Sync. Wird im State B alle paar
 * Sekunden gerufen, um zu prüfen, ob der User auf einem anderen Gerät
 * (z.B. Handy) den Verify-Link bereits geklickt hat.
 */
export const checkVerifyStatus = async (email: string): Promise<{ verified: boolean; jwt?: string }> => {
  try {
    const response = await apiClient.post('/checkout/verify-status', { email });
    return response.data.data;
  } catch (error: any) {
    // Bei Fehlern still still abbrechen — Polling wiederholt sich eh
    return { verified: false };
  }
};

/**
 * Create PayPal Checkout Session (hosted, redirect to PayPal)
 */
export const createPayPalCheckout = async (
  planSlug: string,
  couponCode?: string,
  interval: BillingInterval = 'monthly'
): Promise<{ url: string }> => {
  try {
    const response = await apiClient.post('/subscriptions/paypal-checkout', {
      planSlug,
      couponCode,
      interval
    });
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error creating PayPal checkout:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Erstellen des PayPal-Checkouts');
  }
};

/**
 * Get Checkout Session status (for return page after checkout)
 * Auth required
 */
export const getCheckoutSessionStatus = async (sessionId: string): Promise<{
  status: string;
  paymentStatus: string;
  customerEmail: string | null;
  planSlug: string | null;
}> => {
  try {
    const response = await apiClient.get(`/subscriptions/checkout-status?session_id=${sessionId}`);
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching checkout status:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Abrufen des Checkout-Status');
  }
};

/**
 * Cancel a subscription
 * Auth required
 */
export const cancelSubscription = async (subscriptionId: string): Promise<void> => {
  try {
    await apiClient.post(`/subscriptions/${subscriptionId}/cancel`);
  } catch (error: any) {
    console.error('[SubscriptionService] Error cancelling subscription:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Kündigen des Abonnements');
  }
};

/**
 * Reactivate a cancelled subscription
 * Auth required
 */
export const reactivateSubscription = async (subscriptionId: string): Promise<void> => {
  try {
    await apiClient.post(`/subscriptions/${subscriptionId}/reactivate`);
  } catch (error: any) {
    console.error('[SubscriptionService] Error reactivating subscription:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Reaktivieren des Abonnements');
  }
};

/**
 * Get Stripe Customer Portal URL
 * Auth required
 */
export const getCustomerPortalUrl = async (returnUrl?: string): Promise<string> => {
  try {
    const response = await apiClient.post('/subscriptions/portal', { returnUrl });
    return response.data.data.portalUrl;
  } catch (error: any) {
    console.error('[SubscriptionService] Error getting portal URL:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Erstellen der Portal-Session');
  }
};

/**
 * Check if user has a cancelled but still active subscription that can be reactivated
 * This prevents double-charging when re-subscribing
 * Auth required
 */
export const checkReactivation = async (planSlug: string): Promise<ReactivationCheck> => {
  try {
    const response = await apiClient.get(`/subscriptions/check-reactivation?planSlug=${encodeURIComponent(planSlug)}`);
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error checking reactivation:', error);
    // Return default (no reactivation possible) on error
    return {
      canReactivate: false,
      hasActivePeriod: false,
      subscriptionId: null,
      currentPeriodEnd: null,
      planName: null,
      paymentProvider: null,
      message: null
    };
  }
};

/**
 * Pflicht-Abo-Status abrufen (welche Module aktiv, welche fehlen, verfügbare Plans)
 * Auth required
 */
export const getRequirementStatus = async (): Promise<RequirementStatus> => {
  try {
    const response = await apiClient.get('/subscriptions/required-status');
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching requirement status:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Laden des Pflicht-Abo-Status');
  }
};

// ============================================
// PLAN CHANGE FUNCTIONS
// ============================================

/**
 * Preview a plan change (cost calculation and warnings)
 * Auth required
 */
export const previewPlanChange = async (newPlanSlug: string): Promise<PlanChangePreview> => {
  try {
    const response = await apiClient.get(`/subscriptions/plan-change/preview?newPlan=${encodeURIComponent(newPlanSlug)}`);
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error previewing plan change:', error);
    throw new Error(error.response?.data?.error || 'Fehler bei der Wechselvorschau');
  }
};

/**
 * Execute a plan change
 * Auth required
 */
export const executePlanChange = async (newPlanSlug: string): Promise<PlanChangeResult> => {
  try {
    const response = await apiClient.post('/subscriptions/plan-change/execute', { newPlanSlug });
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error executing plan change:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Plan-Wechsel');
  }
};

/**
 * Cancel a pending downgrade
 * Auth required
 */
export const cancelPendingDowngrade = async (): Promise<void> => {
  try {
    await apiClient.post('/subscriptions/plan-change/cancel-pending');
  } catch (error: any) {
    console.error('[SubscriptionService] Error cancelling pending downgrade:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Stornieren des Plan-Wechsels');
  }
};

// ============================================
// DOWNGRADE RESOURCE SELECTION
// ============================================

export interface ResourceItem {
  id: string;
  name: string;
  subtitle?: string;
  logoPath?: string | null;
  itemCount?: number;
  addedAt: string;
}

export interface UserResourceSummary {
  assets: Array<ResourceItem & {
    symbol: string;
    assetType: 'STOCK' | 'CRYPTOCURRENCY';
    watchlistCount: number;
  }>;
  watchlists: Array<ResourceItem & {
    isDefault: boolean;
    color?: string | null;
  }>;
  youtubeChannels: Array<ResourceItem & {
    channelTitle: string;
    thumbnailPath: string | null;
  }>;
  chartLayouts: Array<ResourceItem & {
    drawingCount: number;
  }>;
  limits: {
    assets: number | null;
    watchlists: number | null;
    youtubeChannels: number | null;
    chartLayouts: number | null;
  };
}

export interface DowngradeResourceSelection {
  id: string;
  userId: string;
  subscriptionId: string;
  pendingPlanId: string;
  retainedAssetIds: string[];
  retainedWatchlistIds: string[];
  retainedYoutubeChannelIds: string[];
  retainedChartLayoutIds: string[];
  status: 'pending' | 'applied' | 'cancelled';
  targetAssetLimit: number | null;
  targetWatchlistLimit: number | null;
  targetYoutubeChannelLimit: number | null;
  targetChartLayoutLimit: number | null;
  createdAt: string;
  updatedAt: string;
  appliedAt: string | null;
}

export interface SaveSelectionInput {
  retainedAssetIds: string[];
  retainedWatchlistIds: string[];
  retainedYoutubeChannelIds: string[];
  retainedChartLayoutIds: string[];
}

/**
 * Alle Ressourcen des Users laden (für Downgrade-Auswahl)
 * Auth required
 */
export const getDowngradeResources = async (): Promise<UserResourceSummary> => {
  try {
    const response = await apiClient.get('/subscriptions/plan-change/resources');
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching downgrade resources:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Laden der Ressourcen');
  }
};

/**
 * Aktuelle Ressourcen-Auswahl laden
 * Auth required
 */
export const getResourceSelection = async (): Promise<DowngradeResourceSelection | null> => {
  try {
    const response = await apiClient.get('/subscriptions/plan-change/resource-selection');
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error fetching resource selection:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Laden der Auswahl');
  }
};

/**
 * Ressourcen-Auswahl speichern
 * Auth required
 */
export const saveResourceSelection = async (input: SaveSelectionInput): Promise<DowngradeResourceSelection> => {
  try {
    const response = await apiClient.post('/subscriptions/plan-change/resource-selection', input);
    return response.data.data;
  } catch (error: any) {
    console.error('[SubscriptionService] Error saving resource selection:', error);
    throw new Error(error.response?.data?.error || 'Fehler beim Speichern der Auswahl');
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format subscription price for display
 */
export const formatSubscriptionPrice = (priceMonthly: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(priceMonthly);
};

/**
 * Check if a plan slug grants Bitcoin access (Stacker oder Whale)
 */
export const hasBitcoinAccess = (planSlug: string): boolean => {
  return planSlug?.startsWith('bitcoin-') ?? false;
};

/**
 * Check if a plan slug grants FCMS access
 */
export const hasFcmsAccess = (planSlug: string): boolean => {
  return planSlug?.startsWith('fcms-') ?? false;
};

/**
 * Get human-readable subscription status
 */
export const getStatusLabel = (status: UserSubscription['status']): string => {
  const labels: Record<UserSubscription['status'], string> = {
    active: 'Aktiv',
    canceled: 'Gekündigt',
    past_due: 'Zahlung ausstehend',
    trialing: 'Testphase',
    expired: 'Abgelaufen'
  };
  return labels[status] || status;
};

/**
 * Get status color class for UI
 */
export const getStatusColor = (status: UserSubscription['status']): string => {
  const colors: Record<UserSubscription['status'], string> = {
    active: 'text-green-600 bg-green-100',
    canceled: 'text-orange-600 bg-orange-100',
    past_due: 'text-red-600 bg-red-100',
    trialing: 'text-blue-600 bg-blue-100',
    expired: 'text-gray-600 bg-gray-100'
  };
  return colors[status] || 'text-gray-600 bg-gray-100';
};

/**
 * Get payment provider label for display
 */
export const getPaymentProviderLabel = (provider: UserSubscription['paymentProvider']): string => {
  if (provider === 'paypal') return 'PayPal';
  if (provider === 'stripe') return 'Karte/SEPA';
  return 'Kostenlos';
};

/**
 * Check if subscription can be reactivated
 * PayPal subscriptions cannot be reactivated after cancellation
 */
export const canReactivate = (subscription: UserSubscription): boolean => {
  return subscription.paymentProvider !== 'paypal';
};

// Default export with all functions
export default {
  getAvailablePlans,
  getSubscriptionStatus,
  getRequirementStatus,
  checkContentAccess,
  checkReactivation,
  createCheckoutSession,
  getCheckoutSessionStatus,
  cancelSubscription,
  reactivateSubscription,
  getCustomerPortalUrl,
  previewPlanChange,
  executePlanChange,
  cancelPendingDowngrade,
  formatSubscriptionPrice,
  hasBitcoinAccess,
  hasFcmsAccess,
  getStatusLabel,
  getStatusColor,
  getPaymentProviderLabel,
  canReactivate
};
